import { flow, types } from 'mobx-state-tree';
import getAppRoot from './utils/getAppRoot';
import {
  selectFromWebhookDeliveryAttemptDto,
  selectFromWebhookDto,
  WebhookDeliveryAttemptDtoModelType,
  WebhookDtoModelType,
  WebhookSecretDtoModelType,
} from './models';

// The organization has at most one webhook, backed entirely by the webhook
// service (Outpost). This store proxies the planning-service GraphQL ops; it
// holds no webhook state beyond the id of the currently-loaded webhook.
const WebhookSelection = selectFromWebhookDto()
  .id.url.label.active.topics.createdAt.createdByUserUuid.createdByName.toString();

const AttemptSelection = selectFromWebhookDeliveryAttemptDto()
  .id.time.topic.status.code.attemptNumber.toString();

// How many of the newest delivery attempts to request. The backend defaults
// and clamps its optional `limit` arg; the card's caption states this number,
// so request and copy can't drift apart.
export const RECENT_ATTEMPTS_LIMIT = 20;

const Webhooks = types
  .model('Webhooks')
  .props({
    isLoading: types.optional(types.boolean, false),
    isSubmitting: types.optional(types.boolean, false),
    currentWebhookId: types.maybe(types.string),
    hasLoaded: types.optional(types.boolean, false),
    isLoadingAttempts: types.optional(types.boolean, false),
    hasLoadedAttempts: types.optional(types.boolean, false),
    attemptsLoadFailed: types.optional(types.boolean, false),
    deliveryAttemptIds: types.optional(types.array(types.string), []),
  })
  .views((self) => ({
    get webhook(): WebhookDtoModelType | null {
      if (!self.currentWebhookId) return null;
      return getAppRoot(self).model.webhookDtos.get(self.currentWebhookId) ?? null;
    },
    get deliveryAttempts(): WebhookDeliveryAttemptDtoModelType[] {
      const attempts = getAppRoot(self).model.webhookDeliveryAttemptDtos;
      return self.deliveryAttemptIds
        .map((id) => attempts.get(id))
        .filter((attempt): attempt is WebhookDeliveryAttemptDtoModelType => !!attempt);
    },
  }))
  .actions((self) => ({
    fetchWebhook: flow(function* () {
      self.isLoading = true;
      try {
        const res = yield getAppRoot(self).model.queryWebhook({}, WebhookSelection, { fetchPolicy: 'no-cache' }).currentPromise();
        self.currentWebhookId = res?.webhook?.id ?? undefined;
      } finally {
        self.isLoading = false;
        self.hasLoaded = true;
      }
    }),
    createWebhook: flow(function* (input: { url: string; label?: string; topics?: string[] }) {
      self.isSubmitting = true;
      try {
        const res = yield getAppRoot(self).model.mutateCreateWebhook({ input }, WebhookSelection).currentPromise();
        self.currentWebhookId = res.createWebhook.id;
        return res.createWebhook as WebhookDtoModelType;
      } finally {
        self.isSubmitting = false;
      }
    }),
    fetchSecret: flow(function* (id: string) {
      const res = yield getAppRoot(self).model
        .queryWebhookSecret({ id }, (qb) => qb.secret, { fetchPolicy: 'no-cache' })
        .currentPromise();
      return res?.webhookSecret as WebhookSecretDtoModelType;
    }),
    rotateSecret: flow(function* (id: string, invalidatePreviousImmediately = false) {
      const res = yield getAppRoot(self).model
        .mutateRotateWebhookSecret({ id, invalidatePreviousImmediately }, (qb) => qb.secret)
        .currentPromise();
      return res.rotateWebhookSecret as WebhookSecretDtoModelType;
    }),
    updateWebhook: flow(function* (input: { id: string; url?: string; label?: string; topics?: string[] }) {
      self.isSubmitting = true;
      try {
        const res = yield getAppRoot(self).model.mutateUpdateWebhook({ input }, WebhookSelection).currentPromise();
        return res.updateWebhook as WebhookDtoModelType;
      } finally {
        self.isSubmitting = false;
      }
    }),
    setActive: flow(function* (id: string, active: boolean) {
      // Optimistic: flip locally so the switch responds immediately.
      const existingWebhook = getAppRoot(self).model.webhookDtos.get(id);
      const previous = existingWebhook?.active;
      // Modify the WebhookDto through its own action: this Webhooks store is not
      // an ancestor of the node, so a direct write trips MST's protection.
      existingWebhook?.setActive(active);
      try {
        yield getAppRoot(self).model.mutateSetWebhookActive({ id, active }, WebhookSelection).currentPromise();
      } catch (error) {
        if (existingWebhook && previous !== undefined) existingWebhook.setActive(previous);
        throw error;
      }
    }),
    sendTest: flow(function* (id: string) {
      const res = yield getAppRoot(self).model.mutateTestWebhook({ id }).currentPromise();
      return res.testWebhook.success as boolean;
    }),
    // Always fetched fresh (no-cache): the list reflects live delivery activity,
    // not state this app mutates. Ordering comes from the backend (newest first).
    fetchDeliveryAttempts: flow(function* (webhookId: string) {
      self.isLoadingAttempts = true;
      self.attemptsLoadFailed = false;
      try {
        const res = yield getAppRoot(self).model
          .queryWebhookDeliveryAttempts({ id: webhookId, limit: RECENT_ATTEMPTS_LIMIT }, AttemptSelection, { fetchPolicy: 'no-cache' })
          .currentPromise();
        const attempts = (res?.webhookDeliveryAttempts ?? []) as WebhookDeliveryAttemptDtoModelType[];
        self.deliveryAttemptIds.replace(attempts.map((attempt) => attempt.id));
      } catch (error) {
        self.attemptsLoadFailed = true;
        throw error;
      } finally {
        self.isLoadingAttempts = false;
        self.hasLoadedAttempts = true;
      }
    }),
  }));

export default Webhooks;
